import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import CounterfindDashboardHeader from './CounterfindDashboardHeader';
import {
  Activity, Users, AlertTriangle, ArrowUpRight, Clock, RefreshCw, Inbox,
  Server, Zap, ListChecks, UserPlus, Shuffle, ShieldAlert, Download,
  Wifi, WifiOff, MinusCircle, TrendingUp, Loader2, AlertCircle,
} from 'lucide-react';

const CONTAINER_CLS =
  'bg-black border border-gray-400 rounded-xl hover:border-gray-300 ' +
  'hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300';

const fmtTime = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }); }
  catch { return ''; }
};
const fmtRel = (iso) => {
  if (!iso) return '';
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return `${Math.floor(diff)}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  } catch { return ''; }
};

const CounterfindOperationsDashboard = ({ baseUserId }) => {
  const apiBase = window.location.origin;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bulkAssignee, setBulkAssignee] = useState('');

  const fetchAll = useCallback(async () => {
    if (!baseUserId) return;
    setLoading(true);
    try {
      const r = await fetch(`${apiBase}/api/counterfind/operations/dashboard?base_user_id=${encodeURIComponent(baseUserId)}`).then(x => x.json());
      setData(r);
    } catch (e) { console.error('Ops fetch failed', e); }
    finally { setLoading(false); }
  }, [baseUserId, apiBase]);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  // Auto-refresh every 30s so the live snapshot stays… live
  useEffect(() => {
    const id = setInterval(fetchAll, 30000);
    return () => clearInterval(id);
  }, [fetchAll]);

  const doBulk = async (action) => {
    if ((action === 'assign' || action === 'reassign') && !bulkAssignee.trim()) {
      toast.warning('Enter an assignee first.');
      return;
    }
    try {
      const res = await fetch(`${apiBase}/api/counterfind/operations/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          base_user_id: baseUserId,
          action,
          assignee: bulkAssignee.trim() || null,
          // Empty listing_ids means "every pending listing" — bulk action acts as a
          // catch-all. Operations is intentional about wide-net actions.
          listing_ids: [],
        }),
      });
      const j = await res.json();
      toast.success(`${action}: updated ${j.updated || 0} listings.`);
      fetchAll();
    } catch { toast.error('Bulk action failed.'); }
  };

  const exportQueue = () => {
    const all = [
      ...(data?.live_queue?.newest || []),
      ...(data?.escalation_queue || []),
      ...(data?.sla_delays?.overdue || []),
    ];
    // De-dupe by id
    const uniq = Array.from(new Map(all.map(l => [l.id, l])).values());
    const cols = ['id', 'product_title', 'brand', 'platform', 'status', 'priority', 'ai_score', 'assignee', 'sla_deadline'];
    const csv = [cols.join(',')].concat(uniq.map(r =>
      cols.map(c => {
        const v = r[c] == null ? '' : String(r[c]).replace(/"/g, '""');
        return /[",\n]/.test(v) ? `"${v}"` : v;
      }).join(',')
    )).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url;
    a.download = `counterfind_ops_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const maxProd = Math.max(1, ...(data?.productivity?.map(p => p.reviews_completed) || [1]));

  return (
    <div className="min-h-screen bg-black text-white p-3 md:p-6" data-testid="counterfind-operations-dashboard">
      <div className="max-w-[1900px] mx-auto space-y-4">
        <CounterfindDashboardHeader roleLabel="Operations" />
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Activity size={26} className="text-cyan-400" />
            <h1 className="text-2xl md:text-3xl font-bold">Counterfind Operations</h1>
            <span className="text-sm text-gray-400">Live queue · team · SLA · platforms</span>
          </div>
          <button
            onClick={fetchAll}
            data-testid="ops-refresh"
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-black border border-gray-600 hover:border-gray-300 rounded-lg transition-colors"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>

        {/* TOP ROW: Live Queue + Team Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          <Container title="Live Queue Snapshot" icon={Zap} testId="live-queue" wide>
            <SubList title="Newest" items={data?.live_queue?.newest} accent="cyan" />
            <SubList title="Highest AI Scores" items={data?.live_queue?.highest_ai_scores} accent="rose" />
            <SubList title="Urgent" items={data?.live_queue?.urgent} accent="amber" />
            <SubList title="Overdue Reviews" items={data?.live_queue?.overdue_reviews} accent="purple" emptyText="No overdue items 🎉" />
          </Container>
          <Container title="Team Activity Monitor" icon={Users} testId="team-activity">
            <div className="grid grid-cols-3 gap-2 mb-3">
              <Stat label="Active" value={data?.team_activity?.active_reviewers ?? 0} tone="emerald" />
              <Stat label="Idle" value={data?.team_activity?.idle_users ?? 0} tone="amber" />
              <Stat label="Team Size" value={data?.team_activity?.total_team_size ?? 0} tone="cyan" />
            </div>
            <div className="space-y-2 max-h-72 overflow-y-auto custom-scrollbar">
              {(data?.team_activity?.reviewers || []).length === 0 && (
                <div className="text-xs text-gray-500 italic py-3 text-center">No activity today yet.</div>
              )}
              {(data?.team_activity?.reviewers || []).map(r => (
                <ReviewerRow key={r.name} r={r} />
              ))}
            </div>
          </Container>
        </div>

        {/* SLA + Escalation row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          <Container title="SLA / Delays" icon={Clock} testId="sla-delays">
            <div className="grid grid-cols-3 gap-2 mb-2">
              <Stat label="Overdue" value={data?.sla_delays?.counts?.overdue ?? 0} tone="rose" />
              <Stat label="Near Deadline" value={data?.sla_delays?.counts?.nearing_deadline ?? 0} tone="amber" />
              <Stat label="Stuck TDN" value={data?.sla_delays?.counts?.stuck_takedowns ?? 0} tone="purple" />
            </div>
            <div className="text-xs uppercase tracking-wider text-gray-400 mt-2 mb-1">Overdue</div>
            <SmallList items={data?.sla_delays?.overdue} emptyText="None — nice work." showSla />
            <div className="text-xs uppercase tracking-wider text-gray-400 mt-3 mb-1">Stuck Takedowns (&gt;48h)</div>
            <SmallList items={data?.sla_delays?.stuck_takedowns} emptyText="None stuck." showSla />
          </Container>

          <Container title="Escalation Queue" icon={ArrowUpRight} testId="escalation-queue">
            <SmallList items={data?.escalation_queue} emptyText="No escalations — pipeline is calm." showStatus />
          </Container>

          <Container title="Client Requests Feed" icon={Inbox} testId="client-requests">
            <div className="text-xs text-gray-400 mb-2">
              {data?.client_requests?.unanswered_count ?? 0} unanswered in last 24h
            </div>
            <SmallList items={data?.client_requests?.feed} emptyText="No new client requests." showAssignee />
          </Container>
        </div>

        {/* Platform Monitoring */}
        <Container title="Platform Monitoring" icon={Server} testId="platform-monitoring">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            {(data?.platform_monitoring?.health || []).map(p => (
              <PlatformHealthCard key={p.platform} p={p} />
            ))}
          </div>
          {(data?.platform_monitoring?.spikes || []).length > 0 && (
            <div className="border-t border-gray-800 pt-3">
              <div className="text-xs uppercase tracking-wider text-gray-400 mb-2 flex items-center gap-1">
                <AlertCircle size={12} className="text-rose-400" /> Counterfeit Spikes (Last 6h)
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {data.platform_monitoring.spikes.map(s => (
                  <div key={s.platform} className="bg-rose-500/10 border border-rose-500/40 rounded-lg p-2.5 text-sm">
                    <div className="text-white">{s.platform}</div>
                    <div className="text-rose-300 text-xs font-semibold">+{s.count_last_6h} listings</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Container>

        {/* Bulk Actions + Productivity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <Container title="Bulk Actions Panel" icon={ListChecks} testId="bulk-actions">
            <div className="flex items-center gap-2 mb-3">
              <input
                value={bulkAssignee}
                onChange={(e) => setBulkAssignee(e.target.value)}
                placeholder="Assignee name (for assign / reassign)"
                data-testid="ops-bulk-assignee"
                className="flex-1 bg-black border border-gray-700 focus:border-gray-400 rounded-lg px-3 py-2 text-sm placeholder-gray-500 outline-none"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <BulkButton icon={UserPlus} label="Assign Cases" onClick={() => doBulk('assign')} testId="ops-bulk-assign" />
              <BulkButton icon={Shuffle} label="Reassign Workloads" onClick={() => doBulk('reassign')} testId="ops-bulk-reassign" />
              <BulkButton icon={ShieldAlert} label="Bulk Submit Takedowns" onClick={() => doBulk('takedown')} testId="ops-bulk-takedown" tone="cyan" />
              <BulkButton icon={Download} label="Export Queue" onClick={exportQueue} testId="ops-bulk-export" />
            </div>
            <div className="text-[11px] text-gray-500 mt-3">
              Tip: leave the assignee blank for the takedown button — it operates on the whole pending queue.
            </div>
          </Container>

          <Container title="Team Productivity" icon={TrendingUp} testId="productivity">
            <div className="text-xs uppercase tracking-wider text-gray-400 mb-2">Reviews completed · last 12h</div>
            <div className="flex items-end gap-1 h-28 mb-3">
              {(data?.productivity || []).map((p, i) => (
                <div key={i} className="flex-1 flex flex-col items-center gap-1 min-w-0" title={`${p.hour}: ${p.reviews_completed} reviews`}>
                  <div className="w-full bg-gray-900 rounded-t relative overflow-hidden h-24">
                    <div className="absolute bottom-0 left-0 right-0 bg-cyan-500/70 rounded-t" style={{ height: `${(p.reviews_completed / maxProd) * 100}%` }} />
                  </div>
                  <span className="text-[9px] text-gray-500 truncate w-full text-center">{p.hour}</span>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <div className="text-gray-400 mb-1">Accuracy trend</div>
                <div className="flex items-end gap-0.5 h-10">
                  {(data?.productivity || []).map((p, i) => (
                    <div key={i} className="flex-1 bg-emerald-500/70 rounded-t" style={{ height: `${p.accuracy_pct}%`, opacity: 0.7 }} title={`${p.accuracy_pct}%`} />
                  ))}
                </div>
              </div>
              <div>
                <div className="text-gray-400 mb-1">Avg takedown speed (min)</div>
                <div className="flex items-end gap-0.5 h-10">
                  {(data?.productivity || []).map((p, i) => {
                    const maxSp = Math.max(...data.productivity.map(x => x.avg_takedown_minutes));
                    return (
                      <div key={i} className="flex-1 bg-amber-500/70 rounded-t" style={{ height: `${(p.avg_takedown_minutes / maxSp) * 100}%`, opacity: 0.7 }} title={`${p.avg_takedown_minutes} min`} />
                    );
                  })}
                </div>
              </div>
            </div>
          </Container>
        </div>
      </div>
    </div>
  );
};

// ----- Sub-components -----

const Container = ({ title, icon: Icon, testId, wide, children }) => (
  <div className={`${CONTAINER_CLS} p-4 ${wide ? 'lg:col-span-2' : ''}`} data-testid={testId}>
    <div className="flex items-center gap-2 mb-3">
      <Icon size={16} className="text-cyan-400" />
      <h2 className="text-sm font-semibold text-white">{title}</h2>
    </div>
    {children}
  </div>
);

const Stat = ({ label, value, tone = 'cyan' }) => {
  const toneClasses = {
    emerald: 'bg-emerald-500/10 border-emerald-500/40 text-emerald-300',
    amber: 'bg-amber-500/10 border-amber-500/40 text-amber-300',
    rose: 'bg-rose-500/10 border-rose-500/40 text-rose-300',
    purple: 'bg-purple-500/10 border-purple-500/40 text-purple-300',
    cyan: 'bg-cyan-500/10 border-cyan-500/40 text-cyan-300',
  };
  return (
    <div className={`border rounded-lg p-2 ${toneClasses[tone]}`}>
      <div className="text-lg font-bold tabular-nums text-white">{value}</div>
      <div className="text-[10px] uppercase tracking-wider">{label}</div>
    </div>
  );
};

const SubList = ({ title, items, accent, emptyText = 'Empty.' }) => {
  const accentClasses = {
    cyan: 'text-cyan-300', rose: 'text-rose-300', amber: 'text-amber-300', purple: 'text-purple-300',
  };
  return (
    <div className="mb-3 last:mb-0">
      <div className={`text-[10px] uppercase tracking-wider mb-1 ${accentClasses[accent] || 'text-gray-400'}`}>{title}</div>
      {!items || items.length === 0 ? (
        <div className="text-xs text-gray-500 italic">{emptyText}</div>
      ) : (
        <div className="space-y-1">
          {items.slice(0, 3).map(l => (
            <div key={l.id} className="text-xs flex items-center justify-between gap-2 border-b border-gray-800 pb-1 last:border-0">
              <div className="min-w-0 flex-1">
                <div className="text-white truncate" title={l.product_title}>{l.product_title}</div>
                <div className="text-gray-500 text-[10px]">{l.platform} · {l.brand} · {l.ai_score}%</div>
              </div>
              <span className="text-[10px] text-gray-400 whitespace-nowrap">{fmtRel(l.created_at)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const SmallList = ({ items, emptyText, showSla, showStatus, showAssignee }) => {
  if (!items || items.length === 0) {
    return <div className="text-xs text-gray-500 italic py-3 text-center border border-gray-800 rounded">{emptyText}</div>;
  }
  return (
    <div className="space-y-1.5 max-h-72 overflow-y-auto custom-scrollbar">
      {items.map(l => (
        <div key={l.id} className="text-xs border-b border-gray-800 pb-1.5 last:border-0">
          <div className="flex items-center justify-between gap-2">
            <div className="text-white truncate flex-1" title={l.product_title}>{l.product_title}</div>
            {showStatus && <span className="text-[10px] text-purple-300">{l.status}</span>}
          </div>
          <div className="flex items-center justify-between text-[10px] text-gray-500">
            <span className="truncate">{l.platform} · {l.brand}</span>
            {showSla && l.sla_deadline && (
              <span className="text-rose-400 whitespace-nowrap">{fmtRel(l.sla_deadline)}</span>
            )}
            {showAssignee && (
              <span className="whitespace-nowrap">{l.assignee || <span className="text-rose-400">Unassigned</span>}</span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

const ReviewerRow = ({ r }) => {
  const statusInfo = {
    online: { icon: Wifi, cls: 'text-emerald-400' },
    idle: { icon: MinusCircle, cls: 'text-amber-400' },
    offline: { icon: WifiOff, cls: 'text-gray-500' },
  }[r.online_status] || { icon: WifiOff, cls: 'text-gray-500' };
  const S = statusInfo.icon;
  return (
    <div className="flex items-center gap-3 text-sm border-b border-gray-800 pb-2 last:border-0">
      <S size={14} className={statusInfo.cls} />
      <div className="min-w-0 flex-1">
        <div className="text-white truncate">{r.name}</div>
        <div className="text-[10px] text-gray-500">
          {r.reviews_today} today · {r.reviews_per_hour}/hr · workload {r.current_workload}
        </div>
      </div>
      <span className="text-[10px] text-gray-500 whitespace-nowrap">{r.last_activity_min_ago}m ago</span>
    </div>
  );
};

const PlatformHealthCard = ({ p }) => {
  const tone = {
    healthy: { cls: 'border-emerald-500/40 bg-emerald-500/5', dot: 'bg-emerald-400' },
    degraded: { cls: 'border-amber-500/40 bg-amber-500/5', dot: 'bg-amber-400' },
    outage: { cls: 'border-rose-500/40 bg-rose-500/5', dot: 'bg-rose-400' },
  }[p.status] || { cls: 'border-gray-700', dot: 'bg-gray-500' };
  return (
    <div className={`border rounded-lg p-2.5 ${tone.cls}`} data-testid={`platform-${p.platform.replace(/\s+/g, '-').toLowerCase()}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm text-white truncate" title={p.platform}>{p.platform}</span>
        <span className={`w-2 h-2 rounded-full ${tone.dot}`} />
      </div>
      <div className="text-[10px] text-gray-400 capitalize">{p.status} · lag {p.scraper_lag_min}m</div>
      {p.note && <div className="text-[10px] text-amber-300 truncate mt-0.5" title={p.note}>{p.note}</div>}
    </div>
  );
};

const BulkButton = ({ icon: Icon, label, onClick, testId, tone }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors border ${
      tone === 'cyan'
        ? 'bg-cyan-600/20 hover:bg-cyan-600/30 border-cyan-500/50 text-cyan-200'
        : 'bg-black border-gray-600 hover:border-gray-300 text-white'
    }`}
  >
    <Icon size={14} /> {label}
  </button>
);

export default CounterfindOperationsDashboard;
